import { Notifications } from "@mui/icons-material";
import {
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Popover,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { apiGetAuth, apiPatchAuth } from "../calls/apiCalls";
import {
  GET_MY_NOTIFICATIONS,
  GET_MY_TASKS,
  MARK_NOTIFICATION_READ,
} from "../constants/apiUrls";
import { localStorageKeys, urlRoutes } from "../constants/page";
import { openTask } from "../utils/taskNavigation";
import { getFormattedDate } from "../utils/utils";

const getTaskTitle = (task) => task?.title || task?.formName || "Untitled task";

const isTaskComplete = (task) =>
  Boolean(task?.completedAt) ||
  ["completed", "complete", "done", "closed"].includes(
    String(task?.status || "").toLowerCase(),
  );

const isTaskOverdue = (task) => {
  if (isTaskComplete(task) || !task?.dueDate) return false;
  return new Date(task.dueDate) < new Date();
};

const normalizeNotification = (item) => ({
  uuid: item.uuid || item.id,
  title: item.title || item.subject || "Notification",
  body: item.body || item.message || item.description || "",
  createdAt: item.createdAt || item.created_at || item.sentAt || item.date,
  readAt: item.readAt || item.read_at || (item.isRead ? item.createdAt : null),
  formSubmitUUID: item.formSubmitUUID || item.form_submit_uuid || null,
  assetUUID: item.assetUUID || item.asset_uuid || null,
  taskUUID: item.taskUUID || item.task_uuid || null,
});

const isNotificationUnread = (item) => !item.readAt && item.isRead !== true;

const formatRelativeTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return getFormattedDate(date);
};

export default function InboxBell() {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user);

  const [anchorEl, setAnchorEl] = useState(null);
  const [tab, setTab] = useState("tasks");
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);

  const open = Boolean(anchorEl);

  const getMyTasks = useCallback(async () => {
    if (!user?.uuid) {
      setTasks([]);
      return;
    }

    setTasksLoading(true);
    try {
      const response = await apiGetAuth(GET_MY_TASKS);
      setTasks(Array.isArray(response) ? response : []);
    } catch {
      setTasks([]);
    } finally {
      setTasksLoading(false);
    }
  }, [user?.uuid]);

  const getMyNotifications = useCallback(async () => {
    if (!user?.uuid) {
      setNotifications([]);
      return;
    }

    setNotificationsLoading(true);
    try {
      const response = await apiGetAuth(GET_MY_NOTIFICATIONS);
      setNotifications(Array.isArray(response) ? response : []);
    } catch {
      setNotifications([]);
    } finally {
      setNotificationsLoading(false);
    }
  }, [user?.uuid]);

  const refreshInbox = useCallback(() => {
    getMyTasks();
    getMyNotifications();
  }, [getMyTasks, getMyNotifications]);

  useEffect(() => {
    const hasToken = Boolean(
      localStorage.getItem(localStorageKeys.accessToken),
    );
    const authReady = Boolean(user?.uuid) || !hasToken;

    if (!authReady) return;

    if (!user?.uuid) {
      setTasks([]);
      setNotifications([]);
      return;
    }

    refreshInbox();
  }, [user?.uuid, refreshInbox]);

  const openTasks = useMemo(
    () => (Array.isArray(tasks) ? tasks.filter((t) => !isTaskComplete(t)) : []),
    [tasks],
  );

  const normalizedNotifications = useMemo(
    () =>
      Array.isArray(notifications)
        ? notifications.map(normalizeNotification)
        : [],
    [notifications],
  );

  const unreadCount =
    normalizedNotifications.filter(isNotificationUnread).length;
  const badgeCount = openTasks.length + unreadCount;

  useEffect(() => {
    if (!open) return;
    if (openTasks.length > 0) setTab("tasks");
    else if (normalizedNotifications.length > 0) setTab("notifications");
    else setTab("tasks");
  }, [open, openTasks.length, normalizedNotifications.length]);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    if (user?.uuid) refreshInbox();
  };

  const handleClose = () => setAnchorEl(null);

  const handleTaskClick = (task) => {
    handleClose();
    openTask(task, navigate);
  };

  const handleNotificationClick = (notification) => {
    setNotifications((prev) =>
      (Array.isArray(prev) ? prev : []).map((item) => {
        const id = item.uuid || item.id;
        if (id !== notification.uuid) return item;
        return {
          ...item,
          readAt: item.readAt || new Date().toISOString(),
          isRead: true,
        };
      }),
    );

    apiPatchAuth(`${MARK_NOTIFICATION_READ}/${notification.uuid}`, {});

    handleClose();

    if (notification.formSubmitUUID) {
      navigate(`${urlRoutes.reportView}?report=${notification.formSubmitUUID}`);
      return;
    }
    if (notification.assetUUID) {
      navigate(`${urlRoutes.assets}?asset=${notification.assetUUID}&tab=Tasks`);
    }
  };

  if (!user?.uuid) return null;

  return (
    <>
      <IconButton
        color="inherit"
        aria-label="Open inbox"
        onClick={handleOpen}
        sx={{ mr: 1 }}
      >
        <Badge
          badgeContent={badgeCount}
          color="error"
          max={9}
          overlap="circular"
        >
          <Notifications />
        </Badge>
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: { width: 400, maxWidth: "calc(100vw - 24px)", mt: 1 },
          },
        }}
      >
        <Box sx={{ px: 2, pt: 1.5, pb: 0 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Inbox
          </Typography>
        </Box>
        <Tabs
          value={tab}
          onChange={(_, value) => setTab(value)}
          variant="fullWidth"
        >
          <Tab
            value="tasks"
            label={`Tasks${openTasks.length ? ` (${openTasks.length})` : ""}`}
          />
          <Tab
            value="notifications"
            label={`Activity${unreadCount ? ` (${unreadCount})` : ""}`}
          />
        </Tabs>
        <Divider />

        {tab === "tasks" ? (
          <>
            <Box sx={{ maxHeight: 360, overflowY: "auto" }}>
              {tasksLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : openTasks.length === 0 ? (
                <Box sx={{ px: 2, py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    You&apos;re all caught up.
                  </Typography>
                </Box>
              ) : (
                <List disablePadding>
                  {openTasks.map((task) => {
                    const overdue = isTaskOverdue(task);
                    const secondary =
                      task.description?.trim() ||
                      (task.dueDate
                        ? `Due ${getFormattedDate(new Date(task.dueDate))}`
                        : "No due date");
                    return (
                      <ListItem key={task.uuid} disablePadding divider>
                        <ListItemButton onClick={() => handleTaskClick(task)}>
                          <ListItemText
                            primary={getTaskTitle(task)}
                            secondary={secondary}
                            primaryTypographyProps={{
                              noWrap: true,
                              fontWeight: 500,
                            }}
                            secondaryTypographyProps={{ noWrap: true }}
                          />
                          {overdue && (
                            <Chip
                              size="small"
                              color="error"
                              label="Overdue"
                              sx={{ ml: 1 }}
                            />
                          )}
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </Box>
            <Divider />
            <Box sx={{ px: 1, py: 0.5 }}>
              <Button
                fullWidth
                onClick={() => {
                  handleClose();
                  navigate(urlRoutes.tasks);
                }}
              >
                View all tasks
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Box sx={{ maxHeight: 360, overflowY: "auto" }}>
              {notificationsLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : normalizedNotifications.length === 0 ? (
                <Box sx={{ px: 2, py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    No new activity.
                  </Typography>
                </Box>
              ) : (
                <List disablePadding>
                  {normalizedNotifications.map((notification) => {
                    const unread = isNotificationUnread(notification);
                    return (
                      <ListItem key={notification.uuid} disablePadding divider>
                        <ListItemButton
                          onClick={() => handleNotificationClick(notification)}
                          sx={{
                            bgcolor: unread ? "action.hover" : "transparent",
                            borderLeft: "3px solid",
                            borderLeftColor: unread
                              ? "error.main"
                              : "transparent",
                          }}
                        >
                          <ListItemText
                            primary={notification.title}
                            secondary={
                              notification.body
                                ? `${notification.body} · ${formatRelativeTime(notification.createdAt)}`
                                : formatRelativeTime(notification.createdAt)
                            }
                            primaryTypographyProps={{
                              noWrap: true,
                              fontWeight: unread ? 700 : 500,
                            }}
                            secondaryTypographyProps={{ noWrap: true }}
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </Box>
            <Divider />
            <Box sx={{ px: 1, py: 0.5 }}>
              <Button
                fullWidth
                onClick={() => {
                  handleClose();
                  navigate(urlRoutes.notifications);
                }}
              >
                View all activity
              </Button>
            </Box>
          </>
        )}
      </Popover>
    </>
  );
}
